// Match-related type definitions
// This Match type is used for all played matches, including quick matches, scheduled matches, league, club, and tournament matches.
import { Timestamp } from "firebase/firestore";
import type { Identifiable, Timestamped, BaseGolferProfile, FirestoreTimestamp } from './base';

// Type for match participants
export interface MatchParticipant extends BaseGolferProfile {
  phoneNumber?: string;
  yearsPlaying?: number;
  bio?: string;
  preferences?: {
    playingFrequency?: string;
    preferredTeeTime?: string;
    skillLevel?: string;
    walkingPreference?: "walk" | "ride" | "either";
    competitivePreference?: string;
    drinkOnCourse?: boolean;
    smokeOnCourse?: boolean;
    musicPreference?: "none" | "low" | "medium" | "high";
    paceOfPlay?: "slow" | "average" | "fast";
    groupSizeMin?: number;
    groupSizeMax?: number;
  };

  equipment?: {
    driver?: string;
    irons?: string;
    putter?: string;
    favoriteBall?: string;
  };
  performanceStats?: {
    averageDrive?: number;
    fairwaysHit?: number;
    greensInRegulation?: number;
    averagePutts?: number;
    scoringAverage?: number;
  };
  availability?: {
    monday?: boolean;
    tuesday?: boolean;
    wednesday?: boolean;
    thursday?: boolean;
    friday?: boolean;
    saturday?: boolean;
    sunday?: boolean;
  };
  isVerified?: boolean;
  isVeteran?: boolean;
  displayProfileToPublic?: boolean;
  displayHandicap?: boolean;
}

export interface Match extends Identifiable, Timestamped {
  golferId?: string; // ID of the golfer who recorded the match (renamed from userId)
  date: FirestoreTimestamp;
  location: string;
  course: string | { id: string; name: string };
  courseId?: string;
  courseName?: string;
  type: MatchType;
  format: MatchFormat; // 'quick' or 'scheduled'
  status: MatchStatus;
  participants: string[] | MatchParticipant[];
  players?: string[]; // Legacy/backward compatibility
  participantDetails?: {
    id: string;
    name: string;
    handicap: number;
    profileImage?: string;
  }[];
  scores?: Record<string, number> | Record<string, HoleScore[]>;
  /** Final numeric scores keyed by playerId e.g. { "uid1": 72, "uid2": 78 } */
  finalScores?: Record<string, number>;
  winner?: string;
  score?: number;
  opponentName?: string;
  opponentId?: string;
  opponentScore?: number;
  par?: number;
  mmrChange?: number;
  currentMmr?: number;
  isWin?: boolean;
  verified?: boolean;
  scorecardUrl?: string;
  photoUrl?: string;
  pendingVerification?: boolean;
  verifiedBy?: string[];
  holeByHoleScores?: HoleScore[];
  teeBoxUsed?: string;
  notes?: string;
  weatherConditions?: string;
  createdBy?: string;
  leagueId?: string; // If it's a league match
  clubhouseId?: string; // If it's a clubhouse match (renamed from clubId)
  isSolo?: boolean; // True if this is a solo round
  scorecardDeadline?: FirestoreTimestamp; // Deadline for submitting scorecard
  /** When the match completed */
  completedAt?: string | FirestoreTimestamp;
  /** When a dispute was filed */
  disputedAt?: string | FirestoreTimestamp;
  /** Scheduled date/time */
  scheduledDate?: string | FirestoreTimestamp;
  /** When a scorecard was submitted */
  scorecardSubmittedAt?: string | FirestoreTimestamp;
  /** User ID of admin who resolved a dispute */
  resolvedBy?: string;
  /** Reason provided when disputing a match */
  disputeReason?: string;
}

export interface HoleScore {
  holeNumber: number;
  playerScore: number;
  opponentScore?: number;
  par: number;
  holesWon?: number; // For match play
  isPlayerBirdie?: boolean;
  isPlayerEagle?: boolean;
  isPlayerBogey?: boolean;
  putts?: number; // Optional data for stats tracking
  fairwayHit?: boolean; // Optional data for stats tracking
  greenInRegulation?: boolean; // Optional data for stats tracking
}

export type MatchType =
  | "casual"
  | "competitive"
  | "league"
  | "club"
  | "tournament";

export type MatchFormat = "quick" | "scheduled" | "match_play";

// Comprehensive match status - canonical source of truth for ALL match states
export type MatchStatus =
  | "completed"
  | "upcoming"
  | "cancelled"
  | "disputed"
  | "awaiting_scorecard"
  | "scheduled"
  | "in_progress"
  | "pending_scorecard"
  | "overdue"
  // From match-requirements.ts
  | "pending"
  | "active"
  | "verified"
  | "under_review"
  // From matchmaking domain
  | "confirmed";

export interface MatchStats {
  totalMatches: number;
  wins: number;
  losses: number;
  avgScore: number;
  bestScore: number;
  worstScore: number;
  currentMmr: number;
  highestMmr: number;
  lowestMmr: number;
}

export interface MatchVerificationRequest extends Identifiable {
  matchId: string;
  requesterId: string;
  requesterName: string;
  recipientId: string;
  recipientName: string;
  date: string;
  status: "pending" | "approved" | "rejected";
  courseName: string;
  matchDate: string;
  score: number;
  opponentScore: number;
}

// Constants for MMR calculations
export const MMR_CONSTANTS = {
  BASE_K_FACTOR: 32, // Base rate of MMR change
  MATCH_TYPE_MULTIPLIERS: {
    casual: 0.25, // Casual rounds have less impact
    competitive: 0.75, // Competitive matches have more impact than casual
    club: 1.0, // Club matches have standard impact
    league: 1.0, // League matches have standard impact
    tournament: 1.5, // Tournaments have higher impact
  },
  RANKING_THRESHOLDS: {
    beginner: {
      min: 0,
      max: 999,
    },
    intermediate: {
      min: 1000,
      max: 1999,
    },
    advanced: {
      min: 2000,
      max: 2499,
    },
    expert: {
      min: 2500,
    },
  },
  INITIAL_MMR: 1000,
  MIN_MMR: 0,
  MAX_MMR: 3000,
};
